/**
 * Painted, panelled entrance doors (lifted from lantern-water's
 * `door-props.js`, `paneledDoor`): bevelled raised panels in three tones of
 * the house's colour, brass rosettes, rails, a pull ring, a kick plate and,
 * on the apothecary (type 1), paired brass botanical scrolls. One shared
 * painted material with vertex colours, so every entrance palette joins one
 * static batch; its grain texture is drawn once per kit.
 */
import * as THREE from 'three';
import type { Ctx } from './kit.ts';

const shades: Readonly<Record<number, string>> = { 1: '#618d78', 3: '#577e91', 4: '#987866', 0: '#7d8971', 2: '#647b82' };

const painted = (ctx: Ctx): THREE.MeshStandardMaterial =>
  ctx.kit.shared('door-props:painted', () => {
    const grain = ctx.kit.shared('door-props:grain', () => {
      const canvas = document.createElement('canvas');
      canvas.width = 128;
      canvas.height = 128;
      const g = canvas.getContext('2d');
      if (g !== null) {
        g.fillStyle = '#e1dfd1';
        g.fillRect(0, 0, 128, 128);
        for (let x = 0; x < 128; x += 1) {
          const n = Math.sin(x * 83.19) * 43758.54;
          g.fillStyle = `rgba(55,66,54,${0.025 + (n - Math.floor(n)) * 0.045})`;
          g.fillRect(x, 0, 1, 128);
        }
        for (let i = 0; i < 260; i += 1) {
          const x = (i * 73) % 127;
          const y = (i * 47) % 127;
          g.fillStyle = i % 3 ? '#fff6d81c' : '#413f3724';
          g.fillRect(x, y, 1, 1 + (i % 4));
        }
      }
      const t = new THREE.CanvasTexture(canvas);
      t.colorSpace = THREE.SRGBColorSpace;
      t.magFilter = THREE.NearestFilter;
      t.minFilter = THREE.NearestMipmapLinearFilter;
      return t;
    });
    return new THREE.MeshStandardMaterial({ map: grain, vertexColors: true, roughness: 0.76 });
  });

const tinted = <G extends THREE.BufferGeometry>(geometry: G, color: THREE.Color): G => {
  const data: number[] = [];
  for (let i = 0; i < geometry.getAttribute('position').count; i += 1) data.push(color.r, color.g, color.b);
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(data, 3));
  return geometry;
};

const add = (parent: THREE.Object3D, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
  const o = new THREE.Mesh(geometry, material);
  o.position.set(x, y, z);
  o.castShadow = true;
  o.receiveShadow = true;
  parent.add(o);
  return o;
};

const shape = (width: number, height: number, corner = 0.02): THREE.Shape => {
  const x = width / 2;
  const y = height / 2;
  const c = corner;
  const s = new THREE.Shape();
  s.moveTo(-x + c, -y);
  s.lineTo(x - c, -y);
  s.lineTo(x, -y + c);
  s.lineTo(x, y - c);
  s.lineTo(x - c, y);
  s.lineTo(-x + c, y);
  s.lineTo(-x, y - c);
  s.lineTo(-x, -y + c);
  s.closePath();
  return s;
};

/** A panelled door leaf in `parent`'s space: its sill at y 0, its face toward +z. `type` picks the palette (house type). */
export const paneledDoor = (ctx: Ctx, parent: THREE.Object3D, type = 1, arched = true, tall = false): void => {
  const m = ctx.m;
  const paint = painted(ctx);
  const panel = (x: number, y: number, z: number, width: number, height: number, color: THREE.Color): void => {
    const s = shape(width, height, Math.min(0.05, width * 0.13));
    const geo = new THREE.ExtrudeGeometry(s, { depth: 0.018, steps: 1, bevelEnabled: true, bevelSegments: 1, bevelSize: 0.014, bevelThickness: 0.01, curveSegments: 1 });
    add(parent, tinted(geo, color), paint, x, y, z);
  };
  const color = new THREE.Color(shades[type] ?? shades[1] ?? '#618d78');
  const lit = color.clone().multiplyScalar(1.24);
  const recess = color.clone().multiplyScalar(0.58);
  const width = arched ? 1.08 : 0.82;
  if (arched) {
    const outline = new THREE.Shape();
    outline.moveTo(-0.54, 0.14);
    outline.lineTo(0.54, 0.14);
    outline.lineTo(0.54, 1.73);
    outline.absarc(0, 1.73, 0.54, 0, Math.PI, false);
    outline.lineTo(-0.54, 0.14);
    const body = new THREE.ShapeGeometry(outline, 16);
    const uv = body.getAttribute('uv');
    for (let i = 0; i < uv.count; i += 1) uv.setXY(i, uv.getX(i) / 1.08 + 0.5, (uv.getY(i) - 0.14) / 2.13);
    add(parent, tinted(body, color), paint, 0, 0, 0.325);
  } else {
    add(parent, tinted(new THREE.BoxGeometry(0.82, 1.94, 0.09), color), paint, 0, 0.98, 0.22);
  }
  const face = arched ? 0.344 : 0.28;
  const cellWidth = width * 0.35;
  const cells: readonly (readonly [number, number])[] = tall
    ? [
        [0.66, 0.4],
        [1.21, 0.56],
      ]
    : [
        [0.64, 0.38],
        [1.09, 0.37],
      ];
  for (const side of [-1, 1]) {
    for (const [y, h] of cells) {
      const x = side * width * 0.225;
      panel(x, y, face, cellWidth + 0.047, h + 0.047, recess);
      panel(x, y, face + 0.014, cellWidth, h, lit);
      panel(x, y, face + 0.04, cellWidth - 0.057, h - 0.07, color);
    }
    add(parent, new THREE.SphereGeometry(0.026, 6, 4), m.brass, side * width * 0.225, tall ? 0.66 : 0.64, face + 0.077);
  }
  const rails: (readonly [number, number, number, number])[] = [
    tall ? [0, 0.97, 0.041, 1.14] : [0, 0.89, 0.041, 1.02],
    [0, tall ? 0.895 : 0.865, width * 0.91, 0.044],
    [0, 0.407, width * 0.96, 0.052],
  ];
  if (tall) rails.push([0, 1.52, width * 0.94, 0.05]);
  for (const [x, y, w, h] of rails) add(parent, tinted(new THREE.BoxGeometry(w, h, 0.035), lit), paint, x, y, face + 0.037);
  if (type === 1) {
    for (const side of [-1, 1]) {
      const x = side * width * 0.225;
      const points = [new THREE.Vector3(x - 0.04, 1.0, face + 0.074), new THREE.Vector3(x + 0.025, 1.085, face + 0.074), new THREE.Vector3(x - 0.018, 1.19, face + 0.074)];
      add(parent, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 10, 0.007, 4, false), m.brass, 0, 0, 0);
      for (let i = 0; i < 4; i += 1) {
        const leaf = add(parent, new THREE.SphereGeometry(0.017, 6, 4), m.brass, x + (i % 2 ? -0.033 : 0.033), 1.035 + i * 0.034, face + 0.075);
        leaf.scale.set(1.5, 0.55, 0.35);
        leaf.rotation.z = i % 2 ? 0.6 : -0.6;
      }
    }
  }
  const handleX = width * 0.34;
  const handleY = 1.01;
  add(parent, new THREE.BoxGeometry(0.075, 0.18, 0.019), m.brass, handleX, handleY, face + 0.075);
  const ring = add(parent, new THREE.TorusGeometry(0.046, 0.01, 5, 12), m.brass, handleX, handleY - 0.012, face + 0.1);
  ring.rotation.x = 0.14;
  for (const y of [handleY - 0.065, handleY + 0.065]) add(parent, new THREE.SphereGeometry(0.013, 5, 3), m.iron, handleX, y, face + 0.09);
  add(parent, new THREE.BoxGeometry(width * 0.88, 0.065, 0.017), m.brass, 0, 0.455, face + 0.06);
};
